
/**
 * BoCapital — Root App Component
 * Wraps with StoreProvider + BrowserRouter, renders shell or login.
 */

import React, { Suspense } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StoreProvider, useAuthState } from './store';
import { ToastProvider } from './components/Toast/Toast';
import ErrorBoundary from './components/ErrorBoundary';
import { isMarketingHost, appUrl, marketingUrl } from './config/hosts';
import './App.css';

// Lazy-load top-level views so the unauthenticated login/home path does not
// ship the full authenticated dashboard bundle to the browser.
const AppShell = React.lazy(() => import('./AppShell'));
// Partner viewer shell — a stripped, read-only operator-console window for
// stakeholders (role='partner'). Code-split so the full operator bundle is
// never shipped to a partner session.
const PartnerShell = React.lazy(() => import('./views/Partner/PartnerShell'));
// Public gold/green marketing landing (replaces the legacy globe Home at /home).
const LandingPage = React.lazy(() => import('./views/Landing/LandingPage'));
const Login = React.lazy(() => import('./views/LoginPage/LoginPage'));
// Privacy / Terms / Cookies (UK GDPR + trading disclosures, 2026-11-02). Served
// on both hosts so the login page and the landing footer can link to them.
const LegalPage = React.lazy(() => import('./views/Legal/LegalPage'));
// Investor portal (separate gold/green tenant; own auth gate + storage keys).
// Code-split so the operator console never ships the portal bundle.
const InvestorShell = React.lazy(() => import('./views/Investor/InvestorShell'));
const InvestorAuthGate = React.lazy(() => import('./views/Investor/InvestorAuthGate'));
// Bare full-bleed chart for server-side screenshots (Telegram alerts). No shell,
// no auth-gate redirect — the headless browser injects the token into storage.
const ChartRender = React.lazy(() => import('./views/ChartRender/ChartRender'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
      gcTime: 300_000,
    },
  },
});

function AuthGate() {
  const { isAuthenticated, role } = useAuthState();

  if (!isAuthenticated) {
    // Marketing now lives on the apex; on the app host unauthenticated users go
    // straight to the login screen rather than bouncing to the landing page.
    return <Navigate to="/login" replace />;
  }

  // Partner viewers get a stripped read-only shell (no sidebar nav, no active
  // setups, scoped read-only Auto-Trader overview) — never the full operator
  // console. Driven off the role claim so it can't be reached by deep link.
  if (String(role || '').trim().toLowerCase() === 'partner') {
    return <PartnerShell />;
  }

  return <AppShell />;
}

// On the apex/www marketing host, every non-marketing path (login, investor,
// dashboard, deep links) is owned by app.bocapital.co.uk — hard-redirect there,
// preserving the path + query so bookmarks survive the split.
function RedirectToApp() {
  const { pathname, search, hash } = useLocation();
  React.useEffect(() => {
    window.location.replace(appUrl(`${pathname}${search}${hash}`));
  }, [pathname, search, hash]);
  return <AppLoader />;
}

// On the app host, /home is canonicalised back to the marketing apex.
function RedirectToMarketing() {
  React.useEffect(() => {
    window.location.replace(marketingUrl('/'));
  }, []);
  return <AppLoader />;
}

// Minimal fallback for the top-level Suspense boundary.
// Once the auth check resolves the correct chunk will mount.
function AppLoader() {
  return <div className="app-shell-skeleton" aria-hidden="true" />;
}

// Public marketing site (apex / www). Only the landing and legal pages live
// here; every other path is the application's and is redirected to
// app.bocapital.co.uk.
// /chart-render stays renderable so headless screenshot URLs never dead-end.
function MarketingRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/cookies" element={<LegalPage doc="cookies" />} />
      <Route path="/chart-render" element={<ChartRender />} />
      <Route path="*" element={<RedirectToApp />} />
    </Routes>
  );
}

// The application (app.bocapital.co.uk, plus localhost/dev and the Capacitor
// shell): full operator + investor route tree.
function AppRoutes() {
  return (
    <Routes>
      <Route path="/home" element={<RedirectToMarketing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy" element={<LegalPage doc="privacy" />} />
      <Route path="/terms" element={<LegalPage doc="terms" />} />
      <Route path="/cookies" element={<LegalPage doc="cookies" />} />
      <Route path="/chart-render" element={<ChartRender />} />
      <Route path="/speed-test" element={<Navigate to="/login" replace />} />
      {/* Investor portal — its OWN gate/tenant, ABOVE the operator catch-all. */}
      <Route
        path="/investor/*"
        element={
          <InvestorAuthGate>
            <InvestorShell />
          </InvestorAuthGate>
        }
      />
      <Route path="/*" element={<AuthGate />} />
    </Routes>
  );
}

export default function App() {
  const marketing = isMarketingHost();
  return (
    <StoreProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <Suspense fallback={<AppLoader />}>
                {marketing ? <MarketingRoutes /> : <AppRoutes />}
              </Suspense>
            </BrowserRouter>
          </ErrorBoundary>
        </ToastProvider>
      </QueryClientProvider>
    </StoreProvider>
  );
}
